/**
 * Core value types shared by the pairing engines, tiebreaks and file formats.
 *
 * Scores are integers in tenths of a point (TRF stores one decimal), so 1.5
 * points is 15. This keeps every comparison exact under any point system.
 */

/** A score in tenths of a point. */
export type Score = number;

export type Colour = 'w' | 'b';

export function otherColour(c: Colour): Colour {
  return c === 'w' ? 'b' : 'w';
}

/**
 * Per-round result codes, following the TRF result column.
 *
 * Played games: '1' win, '=' draw, '0' loss.
 * Played but unrated (for example lasting less than one move):
 *   'W' win, 'D' draw, 'L' loss.
 * Unplayed games against a scheduled opponent: '+' forfeit win, '-' forfeit loss.
 * Rounds without an opponent: 'U' pairing-allocated bye, 'F' full-point bye,
 *   'H' half-point bye, 'Z' zero-point bye.
 * '*' a game that has been paired but has no result yet.
 */
export type ResultCode =
  '1' | '=' | '0' | 'W' | 'D' | 'L' | '+' | '-' | 'U' | 'F' | 'H' | 'Z' | '*';

export const RESULT_CODES: readonly ResultCode[] = [
  '1',
  '=',
  '0',
  'W',
  'D',
  'L',
  '+',
  '-',
  'U',
  'F',
  'H',
  'Z',
  '*',
];

export function isResultCode(s: string): s is ResultCode {
  return (RESULT_CODES as readonly string[]).includes(s);
}

/** True when the game was actually played over the board. */
export function isPlayedResult(r: ResultCode): boolean {
  return r === '1' || r === '=' || r === '0' || r === 'W' || r === 'D' || r === 'L';
}

/** True for the codes that need an opponent (played, forfeited or pending). */
export function needsOpponent(r: ResultCode): boolean {
  return isPlayedResult(r) || r === '+' || r === '-' || r === '*';
}

/** True for rounds with no opponent at all (byes and absences). */
export function isByeResult(r: ResultCode): boolean {
  return r === 'U' || r === 'F' || r === 'H' || r === 'Z';
}

/** The result seen from the opponent's side. */
export function mirrorResult(r: ResultCode): ResultCode {
  switch (r) {
    case '1':
      return '0';
    case '0':
      return '1';
    case 'W':
      return 'L';
    case 'L':
      return 'W';
    case '+':
      return '-';
    case '-':
      return '+';
    default:
      return r;
  }
}

/** Points (in tenths) awarded for each kind of result. */
export interface PointSystem {
  readonly win: Score;
  readonly draw: Score;
  readonly loss: Score;
  readonly forfeitWin: Score;
  readonly forfeitLoss: Score;
  /** Pairing-allocated bye. */
  readonly pab: Score;
  readonly fullBye: Score;
  readonly halfBye: Score;
  readonly zeroBye: Score;
}

export const STANDARD_POINTS: PointSystem = {
  win: 10,
  draw: 5,
  loss: 0,
  forfeitWin: 10,
  forfeitLoss: 0,
  pab: 10,
  fullBye: 10,
  halfBye: 5,
  zeroBye: 0,
};

/** Football-style 3-1-0 scoring. */
export const THREE_ONE_ZERO: PointSystem = {
  win: 30,
  draw: 10,
  loss: 0,
  forfeitWin: 30,
  forfeitLoss: 0,
  pab: 30,
  fullBye: 30,
  halfBye: 10,
  zeroBye: 0,
};

export function pointsFor(r: ResultCode, ps: PointSystem): Score {
  switch (r) {
    case '1':
    case 'W':
      return ps.win;
    case '=':
    case 'D':
      return ps.draw;
    case '0':
    case 'L':
      return ps.loss;
    case '+':
      return ps.forfeitWin;
    case '-':
      return ps.forfeitLoss;
    case 'U':
      return ps.pab;
    case 'F':
      return ps.fullBye;
    case 'H':
      return ps.halfBye;
    case 'Z':
      return ps.zeroBye;
    case '*':
      return 0;
  }
}

/** One round of one player's history. */
export interface RoundEntry {
  /** Opponent's pairing number, or null for byes and absences. */
  readonly opponent: number | null;
  /** Colour played or scheduled, null when there is no game. */
  readonly colour: Colour | null;
  readonly result: ResultCode;
}

export type Title = 'GM' | 'IM' | 'FM' | 'CM' | 'WGM' | 'WIM' | 'WFM' | 'WCM' | 'HM';

export type Sex = 'm' | 'f';

/** Format a score in tenths as a decimal string ("4.5", "10.0"). */
export function formatScore(s: Score): string {
  const neg = s < 0;
  const a = Math.abs(s);
  return `${neg ? '-' : ''}${Math.floor(a / 10)}.${a % 10}`;
}

/** Parse "4.5", "4,5", "4½" or "4" into tenths. Returns NaN on failure. */
export function parseScore(text: string): Score {
  const t = text.trim().replace(',', '.').replace('½', '.5');
  if (t === '') return NaN;
  if (!/^-?\d+(\.\d)?$/.test(t)) {
    const f = Number(t);
    return Number.isFinite(f) ? Math.round(f * 10) : NaN;
  }
  const neg = t.startsWith('-');
  const [ip, fp] = (neg ? t.slice(1) : t).split('.');
  const v = Number(ip) * 10 + (fp === undefined ? 0 : Number(fp));
  return neg ? -v : v;
}
